/**
 * The password rules of the three forms that set one (register, reset, change),
 * mirrored from the API (`server/api/src/auth.rs::check_password`) so the form can
 * say what is wrong before it asks the server — and kept pure so it is testable
 * without a DOM.
 *
 * The mirror is deliberate but one-way: the server stays the authority. A rule it
 * tightens shows up here as a 422 with the server's own sentence, never as a form
 * that quietly accepts what the API rejects.
 */

/** `check_password`: at least 10 characters. */
export const MIN_PASSWORD = 10;

/**
 * Unicode scalar values — what Rust's `chars().count()` counts, and therefore what
 * the server measures. `"…".length` would count UTF-16 units and call a ten-emoji
 * password twenty characters long.
 */
export const passwordLength = (pw: string): number => [...pw].length;

/** Why a new password cannot be submitted yet; `null` = it can. */
export type PasswordProblem = 'short' | 'mismatch';

/**
 * The new password and its repetition. Order matters: "too short" is the more
 * useful of the two messages while the second field is still empty.
 */
export function passwordProblem(pw: string, repeat: string): PasswordProblem | null {
  if (passwordLength(pw) < MIN_PASSWORD) return 'short';
  if (pw !== repeat) return 'mismatch';
  return null;
}

/**
 * `normalise_email` on the client, for the "forgot" form alone: the endpoint answers
 * 200 to anything on purpose (it must not become a "does this person fly with you"
 * oracle), so a typo would otherwise look exactly like a sent mail.
 */
export function emailLooksValid(email: string): boolean {
  const e = email.trim().toLowerCase();
  return e.length >= 5 && e.includes('@') && e.includes('.');
}

/**
 * The reset token out of `?token=…`; `''` when the link carries none. The token is
 * base64url (`[A-Za-z0-9_-]{43}`), so it survives a query string unescaped — but it
 * is read with URLSearchParams anyway, because a hand-rolled parser is how a `+`
 * turns into a space one day.
 */
export function tokenFromSearch(search: string): string {
  return (new URLSearchParams(search).get('token') ?? '').trim();
}
